/**
 * SOLID Review
 * S — Strong. One concern: turn the Application's report into the wire's — page, build, scrub, contract —
 *     and hand it to the beacon.
 * O — Strong. What a report may carry is the wire contract's; this adds only what the page knows.
 * L — Strong. Implements `IPortClientDiagnosticsServer` exactly.
 * I — Strong. One op.
 * D — Strong. The beacon wire socket, the page's facts, `@ml3d/contract/rest` and the ports' Result.
 */
import { clientErrors as wire } from '@ml3d/contract/rest';
import type { IContextRequest } from '../../domain/ports/schemas/ContextRequest';
import type { IPayloadClientError } from '../../domain/ports/schemas/DiagnosticsMoves';
import type { IPortClientDiagnosticsServer } from '../../application/ports/contracts';
import type { TPortOutcome } from '../../application/ports/PortResult';
import type { IPortBeaconClient } from '../contract/ports';

/** What the page knows that the Application does not: where it is, which build it runs, its own origin. */
export interface IServicePageFacts {
  path(): string;
  readonly appVersion: string;
  readonly origin: string;
}

const PATH = '/api/client-errors';
const DONE: TPortOutcome = { success: true, value: undefined };

/**
 * THE CLIENT HALF OF THE WIRE (client error reporting, plan 2026-10-09, EP6).
 *
 * - The page path without its query or fragment (a `?join=` code), cut to the contract's length.
 * - The stack's SAME-ORIGIN lines only: a frame inside an ad or the consent platform's script names
 *   somebody else's code, and says nothing we could fix.
 * - The message and stack scrubbed with the wire contract's own `scrubClientErrorText` — the function the
 *   server applies again on receipt.
 * - Checked against `ClientErrorReport` before it leaves: a report the server would refuse is not sent.
 */
export class ClientDiagnosticsServerAdapter implements IPortClientDiagnosticsServer {
  constructor(private readonly beacon: IPortBeaconClient, private readonly page: IServicePageFacts) {}

  async send(_ctx: IContextRequest, report: IPayloadClientError): Promise<TPortOutcome> {
    const limits = wire.CLIENT_ERROR_LIMITS;
    const body = wire.ClientErrorReport.safeParse({
      ...report,
      message: wire.scrubClientErrorText(report.message, limits.message),
      stack: report.stack === null ? null : this.ownFrames(report.stack, limits.stack),
      pagePath: this.pagePath(limits.pagePath),
      appVersion: this.page.appVersion,
    });
    if (!body.success) return { success: false, error: { code: 'CONTRACT', message: 'the report does not fit the wire contract' } };
    if (!this.beacon.send(PATH, JSON.stringify(body.data))) return { success: false, error: { code: 'UNAVAILABLE', message: 'the browser did not take the report' } };
    return DONE;
  }

  private ownFrames(stack: string, max: number): string | null {
    const own = stack.split('\n').filter((line) => !line.includes('://') || line.includes(this.page.origin)).join('\n');
    return own.trim() === '' ? null : wire.scrubClientErrorText(own, max);
  }

  private pagePath(max: number): string {
    const path = this.page.path().split(/[?#]/)[0]!.replace(/\s/g, '');
    return (path.startsWith('/') ? path : `/${path}`).slice(0, max);
  }
}
